import { createContext, useContext, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, type IconName } from './Icon.js';

export type PillTone    = 'default' | 'cyan' | 'warn' | 'amber' | 'yellow' | 'blue' | 'steel' | 'muted' | 'soft' | 'danger' | 'success' | 'info' | 'neutral' | 'inherited';
export type PillVariant = 'solid' | 'subtle' | 'outline';
export type PillSize    = 'xs' | 'sm' | 'md';

const Ctx = createContext<{ size: PillSize }>({ size: 'sm' });

// ── Colour table ──────────────────────────────────────────────────────────────

type ToneEntry = {
  border: string; text: string;
  bgActive: string; bgHover: string;
  bgSubtle: string;
  solidBg: string; solidText: string;
};

const TONE: Record<PillTone, ToneEntry> = {
  default: {
    border: 'var(--evo-stroke)',        text: 'var(--evo-fg-3)',
    bgActive: 'rgba(255,255,255,0.06)', bgHover: 'rgba(255,255,255,0.04)',
    bgSubtle: 'var(--evo-fill-subtle)', solidBg: 'rgba(255,255,255,0.12)', solidText: 'var(--evo-fg-2)',
  },
  cyan: {
    border: 'var(--evo-cyan)',          text: 'var(--evo-cyan)',
    bgActive: 'var(--evo-cyan-12)',     bgHover: 'rgba(0,250,200,0.08)',
    bgSubtle: 'var(--evo-cyan-12)',     solidBg: 'var(--evo-cyan)',       solidText: 'var(--evo-fg-on-cyan)',
  },
  warn: {
    border: 'var(--evo-amber)',         text: 'var(--evo-amber)',
    bgActive: 'rgba(245,158,11,0.12)', bgHover: 'rgba(245,158,11,0.08)',
    bgSubtle: 'var(--evo-fill-warning)', solidBg: 'var(--evo-amber)',       solidText: 'var(--evo-bg)',
  },
  amber: {
    border: 'var(--evo-amber)',         text: 'var(--evo-amber)',
    bgActive: 'rgba(245,158,11,0.12)', bgHover: 'rgba(245,158,11,0.08)',
    bgSubtle: 'var(--evo-fill-warning)', solidBg: 'var(--evo-amber)',       solidText: 'var(--evo-bg)',
  },
  yellow: {
    border: '#eab308',                  text: '#eab308',
    bgActive: 'rgba(234,179,8,0.12)',  bgHover: 'rgba(234,179,8,0.08)',
    bgSubtle: 'rgba(234,179,8,0.12)',  solidBg: '#eab308',               solidText: '#0a0a0a',
  },
  danger: {
    border: 'var(--evo-danger)',        text: 'var(--evo-danger)',
    bgActive: 'rgba(239,68,68,0.12)',  bgHover: 'rgba(239,68,68,0.08)',
    bgSubtle: 'var(--evo-fill-danger)',  solidBg: 'var(--evo-danger)',      solidText: '#fff',
  },
  success: {
    border: 'var(--evo-green-500)',      text: 'var(--evo-green-500)',
    bgActive: 'rgba(34,197,94,0.12)',  bgHover: 'rgba(34,197,94,0.08)',
    bgSubtle: 'var(--evo-fill-success)',  solidBg: 'var(--evo-green-500)',  solidText: 'var(--evo-bg)',
  },
  info: {
    border: 'var(--evo-blue-300)',      text: 'var(--evo-blue-300)',
    bgActive: 'rgba(91,155,213,0.12)', bgHover: 'rgba(91,155,213,0.08)',
    bgSubtle: 'rgba(91,155,213,0.12)', solidBg: 'var(--evo-blue-300)',   solidText: 'var(--evo-bg)',
  },
  // Pill "Heredado" — dato que vive en la Cuenta; token con valor propio en
  // tema claro (--evo-inherited-mark), no reusar info/blue directo.
  inherited: {
    border: 'var(--evo-inherited-mark)', text: 'var(--evo-inherited-mark)',
    bgActive: 'rgba(91,155,213,0.12)', bgHover: 'rgba(91,155,213,0.08)',
    bgSubtle: 'rgba(91,155,213,0.12)', solidBg: 'var(--evo-inherited-mark)', solidText: 'var(--evo-bg)',
  },
  blue: {
    border: 'var(--evo-blue-300)',      text: 'var(--evo-blue-300)',
    bgActive: 'rgba(91,155,213,0.12)', bgHover: 'rgba(91,155,213,0.08)',
    bgSubtle: 'rgba(91,155,213,0.12)', solidBg: 'var(--evo-blue-300)',   solidText: 'var(--evo-bg)',
  },
  steel: {
    border: 'var(--evo-steel)',         text: 'var(--evo-steel)',
    bgActive: 'rgba(121,137,148,0.12)', bgHover: 'rgba(121,137,148,0.08)',
    bgSubtle: 'rgba(121,137,148,0.12)', solidBg: 'var(--evo-steel)',      solidText: 'var(--evo-bg)',
  },
  muted: {
    border: 'var(--evo-fg-3)',          text: 'var(--evo-fg-3)',
    bgActive: 'rgba(154,154,154,0.12)', bgHover: 'rgba(154,154,154,0.08)',
    bgSubtle: 'var(--evo-fill-subtle)', solidBg: 'var(--evo-fg-3)',       solidText: 'var(--evo-bg)',
  },
  soft: {
    border: 'var(--evo-fg-2)',          text: 'var(--evo-fg-2)',
    bgActive: 'rgba(184,184,184,0.12)', bgHover: 'rgba(184,184,184,0.08)',
    bgSubtle: 'var(--evo-fill-subtle-strong)', solidBg: 'var(--evo-fg-2)',       solidText: 'var(--evo-bg)',
  },
  neutral: {
    border: 'var(--evo-stroke)',         text: 'var(--evo-text-muted)',
    bgActive: 'rgba(38,47,54,0.30)',    bgHover: 'rgba(38,47,54,0.20)',
    bgSubtle: 'var(--evo-panel)',    solidBg: 'var(--evo-bg-2)',        solidText: 'var(--evo-text-muted)',
  },
};

// ── Size tables ───────────────────────────────────────────────────────────────

const H:   Record<PillSize, string> = { xs: '18px', sm: '22px', md: '26px' };
const PX:  Record<PillSize, string> = { xs: '8px',  sm: '8px',  md: '10px' };
const FS:  Record<PillSize, string> = { xs: '10px', sm: '11px', md: '12px' };
const ICN: Record<PillSize, number> = { xs: 8,      sm: 10,     md: 11     };
const GAP: Record<PillSize, number> = { xs: 3,      sm: 4,      md: 5      };

// ── PillGroup ─────────────────────────────────────────────────────────────────

interface PillGroupProps {
  children: ReactNode;
  size?: PillSize;
}

export function PillGroup({ children, size = 'sm' }: PillGroupProps) {
  return (
    <Ctx.Provider value={{ size }}>
      <div style={{ display: 'inline-flex', gap: 'var(--evo-space-2)', flexShrink: 0 }}>
        {children}
      </div>
    </Ctx.Provider>
  );
}

// ── Pill ──────────────────────────────────────────────────────────────────────

export interface PillProps {
  tone?: PillTone;
  variant?: PillVariant;
  size?: PillSize;
  icon?: ReactNode;
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  onRemove?: () => void;
  children: ReactNode;
}

export function Pill({
  tone = 'default',
  variant = 'outline',
  size: sizeProp,
  icon,
  active,
  disabled,
  onClick,
  onRemove,
  children,
}: PillProps) {
  const { t: tr } = useTranslation();
  const { size: ctxSize } = useContext(Ctx);
  const size = sizeProp ?? ctxSize;
  const t = TONE[tone];

  let bg: string;
  let border: string;
  let color: string;

  if (variant === 'solid') {
    bg     = t.solidBg;
    border = 'transparent';
    color  = t.solidText;
  } else if (variant === 'subtle') {
    bg     = t.bgSubtle;
    border = 'transparent';
    color  = t.text;
  } else {
    bg     = active ? t.border : 'transparent';
    border = t.border;
    color  = active ? 'var(--evo-bg)' : t.text;
  }

  const iconSz = ICN[size];

  return (
    <div
      onClick={!disabled ? onClick : undefined}
      role={onClick ? 'button' : undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: GAP[size],
        paddingLeft: PX[size],
        paddingRight: onRemove ? '4px' : PX[size],
        height: H[size],
        fontSize: FS[size],
        fontWeight: 500,
        fontFamily: 'var(--evo-font)',
        lineHeight: 1,
        color,
        background: bg,
        border: border === 'transparent' ? '1px solid transparent' : `1px solid ${border}`,
        borderRadius: 100,
        cursor: onClick ? (disabled ? 'not-allowed' : 'pointer') : 'default',
        opacity: disabled ? 0.5 : 1,
        whiteSpace: 'nowrap',
        userSelect: 'none',
        flexShrink: 0,
      }}
    >
      {icon != null && (
        typeof icon === 'string'
          ? <Icon name={icon as IconName} size={iconSz} />
          : <span style={{ display: 'inline-flex', lineHeight: 0, flexShrink: 0 }}>{icon}</span>
      )}
      {children}
      {onRemove && (
        <span
          onClick={(e) => { e.stopPropagation(); if (!disabled) onRemove(); }}
          role="button"
          aria-label={tr('common.remove')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '14px',
            height: '14px',
            marginLeft: '2px',
            borderRadius: '2px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: 0.6,
            fontSize: '10px',
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          ×
        </span>
      )}
    </div>
  );
}
